import { useState } from "react";
import { useApi } from "@/lib/useApi";
import { Link } from "react-router-dom";
import { Heart, MapPin, Search, Radio, Building2, User } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/AppContext";
import { useLiveEvent, useRealtime } from "@/context/RealtimeContext";
import { PageHead } from "@/pages/app/Dashboard";
import { Input } from "@/components/ui/input";
import { BadgeCheck } from "lucide-react";

export const FeedCard = ({ item, onLike, fresh }) => {
  const { t } = useLang();
  const p = item.principal || {};
  const Icon = p.kind === "company" ? Building2 : User;
  return (
    <article className={`rounded-md border bg-[#111827] p-6 transition-all duration-500 hover:-translate-y-0.5 ${fresh ? "border-emerald-400/60 shadow-[0_0_30px_rgba(16,185,129,0.15)]" : "border-white/[0.08]"}`} data-testid={`feed-card-${item.id}`}>
      <div className="flex items-start justify-between gap-4">
        <Link to={`/app/profiles/${p.id}`} className="flex items-center gap-3" data-testid={`feed-profile-link-${item.id}`}>
          <span className="grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-[#0B0F17]"><Icon className="h-4 w-4 text-cyan-300" /></span>
          <span>
            <span className="flex items-center gap-1.5 text-sm font-semibold text-white hover:text-emerald-300">{p.name}{p.verified && <BadgeCheck className="h-4 w-4 text-emerald-400" />}</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("trust")} {p.trust_score} · {p.headline}</span>
          </span>
        </Link>
        <div className="flex items-center gap-2">
          {fresh && <span className="rounded-full bg-emerald-400/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-emerald-300">{t("feed_new")}</span>}
          <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${item.type === "hire" ? "border-yellow-400/40 text-yellow-300" : "border-cyan-400/40 text-cyan-300"}`}>{t(item.type === "hire" ? "hiring" : "seeking")}</span>
        </div>
      </div>
      <h3 className="mt-5 font-display text-xl font-bold text-white">{item.title}</h3>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {item.skills.map((s) => <span key={s} className="rounded bg-white/5 px-2 py-0.5 font-mono text-[11px] text-slate-300">{s}</span>)}
      </div>
      <div className="mt-5 flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs text-slate-400"><MapPin className="h-3.5 w-3.5" />{item.location || "—"}{item.remote && " · Remote"} · {item.sector || "—"}{item.openings ? ` · ${item.openings} ${t("openings").toLowerCase()}` : ""}</p>
        {item.mine ? <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("yours")} · {item.likes} {t("likes")}</span> : (
          <button onClick={() => onLike(item.id)} data-testid={`feed-like-btn-${item.id}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-all duration-200 hover:scale-105 ${item.liked_by_me ? "border-rose-400/50 bg-rose-400/10 text-rose-300" : "border-white/10 text-slate-400 hover:text-rose-300"}`}>
            <Heart className={`h-4 w-4 ${item.liked_by_me ? "fill-current" : ""}`} /><span data-testid={`feed-like-count-${item.id}`}>{item.likes}</span>
          </button>
        )}
      </div>
    </article>
  );
};

export default function Feed() {
  const { t } = useLang();
  const { connected } = useRealtime();
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [fresh, setFresh] = useState(new Set());
  const { data: items, setData: setItems } = useApi("/feed", { initial: [], params: { ...(kind && { kind }), ...(q && { q }) }, delay: 250 });

  useLiveEvent(["feed_new", "feed_like"], (m) => {
    if (m.kind === "feed_like") return setItems((xs) => xs.map((x) => (x.id === m.data.id ? { ...x, likes: m.data.likes } : x)));
    const it = m.data;
    if (kind && it.principal?.kind !== kind) return;
    setItems((xs) => (xs.some((x) => x.id === it.id) ? xs : [{ ...it, mine: false, liked_by_me: false }, ...xs]));
    setFresh((s) => new Set(s).add(it.id));
  });

  const like = async (id) => {
    try { const { data } = await api.post(`/feed/${id}/like`); setItems((xs) => xs.map((x) => (x.id === id ? data : x))); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHead title={t("feed_title")} sub={t("feed_sub")}>
        <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${connected ? "border-emerald-400/40 text-emerald-300" : "border-white/10 text-slate-500"}`} data-testid="feed-live-indicator">
          <Radio className={`h-3.5 w-3.5 ${connected ? "pulse-node" : ""}`} />{t("live")}
        </span>
      </PageHead>
      <div className="mb-8 flex flex-wrap items-center gap-3">
        {[["", "feed_all"], ["person", "feed_people"], ["company", "feed_companies"]].map(([k, l]) => (
          <button key={l} onClick={() => setKind(k)} data-testid={`feed-filter-${k || "all"}`}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${kind === k ? "border-emerald-400 bg-emerald-400/10 text-emerald-300" : "border-white/10 text-slate-400 hover:text-white"}`}>{t(l)}</button>
        ))}
        <div className="relative ml-auto w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("feed_search")} data-testid="feed-search-input" />
        </div>
      </div>
      {!items.length && <p className="text-sm text-slate-500" data-testid="feed-empty">{t("empty")}</p>}
      <div className="grid gap-4 lg:grid-cols-2">
        {items.map((it) => <FeedCard key={it.id} item={it} onLike={like} fresh={fresh.has(it.id)} />)}
      </div>
    </div>
  );
}
